import { Theme, Grid, Paper } from "@mui/material";
import Box from '@mui/material/Box';
import React, { useState, ChangeEvent, useEffect } from "react";
import { goBack, push } from "redux-first-history";
import { HTTPRequestStatus, PaymentType } from "../../../utils/models";
import FormLayout from "../../../components/FormLayout";
import CustomButton from "../../../components/CustomButton";
import CreditCardPayment from "./CreditCardPayment";
import RadioQuestions from "../../../components/RadioQuestions";
import { PayBalanceType } from "../../../model/final_balance/pay_balance_types.d";
import { useDispatch, useSelector } from "react-redux";
import { paymentSlice } from "../../../store/reducer/payment";
import { toDollar } from "../../../utils/dataConverter";
import { ErrorAlert } from "../../../components/Alerts";
import { useCollectionYear } from "../../../hooks/User";
import { UserChoice } from "../../../model/final_balance/user_choice";
import { TaxCalculations } from "../../../model/tax_calculations";
import { RootState } from "../../../store/index";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import { usePaymentContainerContext } from "../PaymentContainer";
import { paymentConfig } from "../../../config/paymentConfig";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import PayLater from './PayLater';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_KEY as string);

const PaymentInformation = () => {
  const dispatch = useDispatch();
  const collectionYear = useCollectionYear();
  const { alertMessage, paymentIntentStatus, paymentToken } = useSelector(
    (appState: any) => appState.payment
  );
  const { taxReturnID } = useSelector((rootState: RootState) => rootState.state);
  const [paymentType, setPaymentType] = useState(PaymentType.CreditCard);
  const [balanceSelection, setBalanceSelection] = useState(PayBalanceType.Balance);
  const { taxCalculations } = usePaymentContainerContext();
  const stripePaymentOptions: StripeElementsOptions = {
    clientSecret: paymentToken,
    appearance: {
      variables: {
        colorPrimary: '#0F7580',
        colorDanger: '#E19393',
        fontFamily: 'Source Sans Pro, sans-serif',
      }
    },
  };

  const handleChangeRadio = (event: ChangeEvent<HTMLInputElement>) => {
    let option = event.target.value as PayBalanceType;
    setBalanceSelection(option);
  };

  useEffect(() => {
    // Due to dispatch timing, don't call this if we don't have an ID
    if (taxReturnID !== null) {
      dispatch(paymentSlice.actions.fetchPaymentIntent({
        taxReturnID: taxReturnID,
        includesEstimatedPayment: balanceSelection === PayBalanceType.BalanceWithEstimate
      }));
    }
  }, [taxReturnID, balanceSelection, dispatch]);

  const radioOptions: UserChoice[] = [
    {
      id: PayBalanceType.Balance,
      label: `${collectionYear} tax balance: ${toDollar(taxCalculations.final_balance)}.`,
    },
    {
      id: PayBalanceType.BalanceWithEstimate,
      label: `${collectionYear} tax balance with ${collectionYear + 1} estimated payment:
        ${toDollar(taxCalculations.final_balance_with_quarterly_estimated_payment)}.`,
    },
  ];

  const getBalanceFromPayBalanceType = (balanceType: PayBalanceType, calculations: TaxCalculations) => {
    if (balanceType === PayBalanceType.BalanceWithEstimate) {
      return calculations.final_balance_with_quarterly_estimated_payment;
    } else {
      return calculations.final_balance;
    }
  }

  return (
    <>
      <FormLayout
        title={"Your Balance Due: " + toDollar(getBalanceFromPayBalanceType(balanceSelection, taxCalculations))}
        subtitle="payment"
        description="You may choose to pay this balance right now, or pay later outside of LIME. Review your options below and make a selection."
      >
        <Grid container size={12} sx={{ paddingTop: '16px', paddingBottom: '32px' }} spacing={2}>
          {alertMessage.length > 0 && <ErrorAlert>{alertMessage}</ErrorAlert>}

          {(paymentIntentStatus === HTTPRequestStatus.Requesting) ?
            <LineLoader /> :
            <Grid size={{ md: 12, lg: 6 }} height={'100%'}>
              <Paper elevation={0} className='paper-container'>

                <h2 className='payment-card-title'>Pay Now</h2>
                <p>Enter your credit/debit card information below and select "submit payment" to complete both your tax filing and payment responsibilities. Then be sure to check your E-mail for a payment receipt.</p>
                {!paymentConfig.hideEstimatedPaymentOption && taxCalculations.final_balance > 500 &&
                  <Grid size={12}>
                    <RadioQuestions
                      question={`You have the option of paying your ${collectionYear} tax balance or including an estimated payment of ${toDollar(taxCalculations.quarterly_estimated_tax)} for the first quarter of ${Number(collectionYear) + 1}.`}
                      radioOptions={radioOptions}
                      handleChangeRadio={handleChangeRadio}
                      radioValue={balanceSelection}
                    />
                  </Grid>
                }
                <h3 className="no-top-margin">Payment Method</h3>
                {paymentType === PaymentType.CreditCard && paymentToken.length > 0 ? (
                  <Elements stripe={stripePromise} options={stripePaymentOptions}>
                    <CreditCardPayment />
                  </Elements>
                ) : <p>Please wait while begin the payment process...</p>
                }
              </Paper>
            </Grid>
          }

          {!!taxCalculations.final_balance && <Grid size={{ md: 12, lg: 6 }} height={'100%'} >
            <PayLater
              collectionYear={collectionYear}
              taxReturnID={taxReturnID}
              taxCalculations={taxCalculations}
            />
          </Grid>}
        </Grid>
      </FormLayout>
    </>
  );
};

export default PaymentInformation;
